// src/pages/RMRDC/RmrdcOrders.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  Badge,
  Stack,
  Tab,
  Tabs,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Switch,
  CircularProgress,
  Skeleton,
  Autocomplete,
  Rating,
  CardActions,
  Pagination,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  ShoppingBag as ShoppingBagIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  LocalShipping as LocalShippingIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Download as DownloadIcon,
  FilterAlt as FilterAltIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { formatDate, formatCurrency } from '../buyers/components/utils';

// ============================================
// COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  gold: '#D4AF37',
  purple: '#6A1B9A',
  teal: '#00838F',
  pink: '#C2185B',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

const STATUS_COLORS = {
  pending: { bg: '#FFF3E0', text: '#E65100', icon: <ScheduleIcon /> },
  processing: { bg: '#E3F2FD', text: '#0D47A1', icon: <RefreshIcon /> },
  shipped: { bg: '#F3E5F5', text: '#6A1B9A', icon: <LocalShippingIcon /> },
  delivered: { bg: '#E8F5E9', text: '#1B5E20', icon: <CheckCircleIcon /> },
  cancelled: { bg: '#FFEBEE', text: '#B71C1C', icon: <CancelIcon /> },
};

const PAYMENT_STATUS_COLORS = {
  pending: { bg: '#FFF3E0', text: '#E65100' },
  paid: { bg: '#E8F5E9', text: '#1B5E20' },
  failed: { bg: '#FFEBEE', text: '#B71C1C' },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost/rawMaterialChemicalDb/uploads/';

const API_ENDPOINTS = {
  RMRDC: {
    ORDERS: `${API_BASE_URL}/rmrdc/orders.php`,
    EXPORT: `${API_BASE_URL}/rmrdc/orders.php?action=export`,
    BULK: `${API_BASE_URL}/rmrdc/orders.php?action=bulk`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[ORDERS-MGMT] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[ORDERS-MGMT] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[ORDERS-MGMT] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[ORDERS-MGMT] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getStatusChip = (status) => {
  const config = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    ...config,
  };
};

const getPaymentStatusChip = (status) => {
  const config = PAYMENT_STATUS_COLORS[status] || PAYMENT_STATUS_COLORS.pending;
  return {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    ...config,
  };
};

// ============================================
// MAIN COMPONENT
// ============================================
const RmrdcOrders = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'super_admin';
  const isAdmin = userType === 'super_admin' || userType === 'rmrdc';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [displayOrders, setDisplayOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('orders');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);

  // Edit form state
  const [editOrderData, setEditOrderData] = useState({
    id: null,
    status: 'pending',
    payment_status: 'pending',
    notes: '',
  });

  // ============================================
  // CLIENT-SIDE FILTERING & SEARCH
  // ============================================
  const applyFilters = useCallback(() => {
    let result = [...orders];

    // Client-side search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(order => {
        const searchableFields = [
          order.order_reference,
          order.buyer_name,
          order.buyer_organisation,
          order.shipping_address,
          order.id?.toString(),
        ].filter(Boolean);
        
        return searchableFields.some(field => 
          String(field).toLowerCase().includes(searchLower)
        );
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    // Payment status filter
    if (paymentStatusFilter !== 'all') {
      result = result.filter(order => order.payment_status === paymentStatusFilter);
    }

    // Date range filter
    if (dateFrom) {
      result = result.filter(order => {
        const orderDate = new Date(order.order_date).toISOString().split('T')[0];
        return orderDate >= dateFrom;
      });
    }
    if (dateTo) {
      result = result.filter(order => {
        const orderDate = new Date(order.order_date).toISOString().split('T')[0];
        return orderDate <= dateTo;
      });
    }

    // Sort
    const sortMap = {
      'newest': (a, b) => new Date(b.order_date) - new Date(a.order_date),
      'oldest': (a, b) => new Date(a.order_date) - new Date(b.order_date),
      'amount_high': (a, b) => (b.total_amount || 0) - (a.total_amount || 0),
      'amount_low': (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
    };

    if (sortMap[sortBy]) {
      result.sort(sortMap[sortBy]);
    }

    setFilteredOrders(result);
    setTotalCount(result.length);
    setDisplayOrders(result.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
  }, [orders, searchTerm, statusFilter, paymentStatusFilter, dateFrom, dateTo, sortBy, page, rowsPerPage]);

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchOrders = useCallback(async () => {
    logger.info('Fetching orders...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.RMRDC.ORDERS}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        const ordersData = result.data || [];
        setOrders(ordersData);
        setFilteredOrders(ordersData);
        setTotalCount(ordersData.length);
        setDisplayOrders(ordersData.slice(0, rowsPerPage));
        setLastRefreshed(new Date());
        
        logger.info(`✅ Loaded ${ordersData.length} orders`);
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load orders. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage]);

  const updateOrderStatus = useCallback(async (orderId, status, notes = '') => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;
      const userData = JSON.parse(userDataStr);

      const response = await fetch(`${API_ENDPOINTS.RMRDC.ORDERS}?user_id=${userData.id}&id=${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          notes,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Order #${orderId} status updated to ${status}`,
          severity: 'success',
        });
        await fetchOrders();
        setViewDialogOpen(false);
        setEditDialogOpen(false);
      } else {
        throw new Error(result.message || 'Failed to update order');
      }
    } catch (error) {
      logger.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update order',
        severity: 'error',
      });
    }
  }, [fetchOrders]);

  const bulkUpdateOrders = useCallback(async () => {
    if (selectedOrders.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one order',
        severity: 'warning',
      });
      return;
    }

    if (!bulkAction) {
      setSnackbar({
        open: true,
        message: 'Please select an action',
        severity: 'warning',
      });
      return;
    }

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;
      const userData = JSON.parse(userDataStr);

      const response = await fetch(`${API_ENDPOINTS.RMRDC.BULK}&user_id=${userData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bulk_action: bulkAction,
          order_ids: selectedOrders,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success',
        });
        setSelectedOrders([]);
        setBulkAction('');
        setBulkActionDialog(false);
        await fetchOrders();
      } else {
        throw new Error(result.message || 'Bulk operation failed');
      }
    } catch (error) {
      logger.error('Error performing bulk action:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Bulk operation failed',
        severity: 'error',
      });
    }
  }, [selectedOrders, bulkAction, fetchOrders]);

  const deleteOrder = useCallback(async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;
      const userData = JSON.parse(userDataStr);

      const response = await fetch(`${API_ENDPOINTS.RMRDC.ORDERS}?user_id=${userData.id}&id=${orderId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hard_delete: false }),
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Order #${orderId} cancelled successfully`,
          severity: 'success',
        });
        setDeleteDialogOpen(false);
        await fetchOrders();
      } else {
        throw new Error(result.message || 'Failed to cancel order');
      }
    } catch (error) {
      logger.error('Error cancelling order:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to cancel order',
        severity: 'error',
      });
    }
  }, [fetchOrders]);

 
  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || (userType !== 'super_admin' && userType !== 'rmrdc')) {
      navigate('/RawAuth');
      return;
    }
    fetchOrders();
  }, [fetchOrders, navigate, userType]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handlePaymentStatusFilterChange = (event) => {
    setPaymentStatusFilter(event.target.value);
    setPage(0);
  };

  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
    setPage(0);
  };

  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
    setPage(0);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const start = newPage * rowsPerPage;
    setDisplayOrders(filteredOrders.slice(start, start + rowsPerPage));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setDisplayOrders(filteredOrders.slice(0, newRowsPerPage));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOrders(filteredOrders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleEditOrder = (order) => {
    setEditOrderData({
      id: order.id,
      status: order.status || 'pending',
      payment_status: order.payment_status || 'pending',
      notes: '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    if (editOrderData.id) {
      updateOrderStatus(
        editOrderData.id,
        editOrderData.status,
        editOrderData.notes
      );
    }
  };

  const handleDeleteOrder = (order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchOrders();
    setSnackbar({
      open: true,
      message: 'Orders refreshed',
      severity: 'info',
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPaymentStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('newest');
    setPage(0);
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderFilters = () => (
    <Paper
      sx={{
        p: 2.5,
        mb: 3.5,
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        background: '#FFFFFF',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search orders by reference, buyer..."
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <CloseIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="shipped">Shipped</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={paymentStatusFilter}
            onChange={handlePaymentStatusFilterChange}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all">All Payment</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          type="date"
          size="small"
          label="From"
          value={dateFrom}
          onChange={handleDateFromChange}
          sx={{ minWidth: 140 }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          type="date"
          size="small"
          label="To"
          value={dateTo}
          onChange={handleDateToChange}
          sx={{ minWidth: 140 }}
          InputLabelProps={{ shrink: true }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="amount_high">Amount: High to Low</MenuItem>
            <MenuItem value="amount_low">Amount: Low to High</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
      
           
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{ bgcolor: COLORS.grey[100] }}
            >
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
          {(searchTerm || statusFilter !== 'all' || paymentStatusFilter !== 'all' || dateFrom || dateTo) && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearFilters}
              sx={{ borderRadius: 2, textTransform: 'none' }}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );

  const renderBulkActions = () => {
    if (selectedOrders.length === 0) return null;

    return (
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          bgcolor: alpha(COLORS.primary, 0.04),
          border: `1px solid ${alpha(COLORS.primary, 0.2)}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {selectedOrders.length} orders selected
        </Typography>
        <Divider orientation="vertical" flexItem />
        <Button
          size="small"
          variant="outlined"
          color="info"
          onClick={() => {
            setBulkAction('process');
            setBulkActionDialog(true);
          }}
        >
          Process
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setBulkAction('ship');
            setBulkActionDialog(true);
          }}
          sx={{ color: COLORS.purple, borderColor: COLORS.purple }}
        >
          Ship
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="success"
          onClick={() => {
            setBulkAction('deliver');
            setBulkActionDialog(true);
          }}
        >
          Deliver
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => {
            setBulkAction('cancel');
            setBulkActionDialog(true);
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={() => setSelectedOrders([])}
        >
          Clear
        </Button>
      </Paper>
    );
  };

  const renderOrdersTable = () => {
    if (loading && orders.length === 0) {
      return (
        <Paper sx={{ borderRadius: 4, p: 3, border: `1px solid ${COLORS.grey[200]}` }}>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rounded" height={60} sx={{ mb: 1 }} />
          ))}
        </Paper>
      );
    }

    if (filteredOrders.length === 0) {
      return (
        <Paper sx={{ borderRadius: 4, p: 8, textAlign: 'center', border: `1px solid ${COLORS.grey[200]}` }}>
          <ReceiptIcon sx={{ fontSize: 60, color: COLORS.grey[400], mb: 2 }} />
          <Typography variant="h6" color="textSecondary" fontWeight={600}>
            No orders found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Try adjusting your filters or search terms
          </Typography>
          <Button
            variant="contained"
            onClick={handleClearFilters}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Clear Filters
          </Button>
        </Paper>
      );
    }

    return (
      <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${COLORS.grey[200]}` }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                    indeterminate={selectedOrders.length > 0 && selectedOrders.length < filteredOrders.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Request ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Buyer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayOrders.map((order) => {
                const statusChip = getStatusChip(order.status);
                const paymentChip = getPaymentStatusChip(order.payment_status);
                const isSelected = selectedOrders.includes(order.id);

                return (
                  <TableRow
                    key={order.id}
                    hover
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(COLORS.primary, 0.02),
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectOrder(order.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="primary">
                        {order.order_reference || `ORD-${order.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {order.buyer_name || 'Guest'}
                        </Typography>
                        {order.buyer_organisation && (
                          <Typography variant="caption" color="textSecondary">
                            {order.buyer_organisation}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="primary">
                        {formatCurrency(order.total_amount, order.currency)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {order.total_items || 0} items
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusChip.label}
                        sx={{
                          bgcolor: statusChip.bg,
                          color: statusChip.text,
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: statusChip.text },
                        }}
                        icon={statusChip.icon}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={paymentChip.label}
                        sx={{
                          bgcolor: paymentChip.bg,
                          color: paymentChip.text,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(order.order_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                            sx={{ color: COLORS.primary }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                       
                       
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && orders.length === 0) {
    return <Preloader text="Loading Orders..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        userData={userData}
        onLogout={() => {
          localStorage.clear();
          navigate('/RawAuth');
        }}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={() => {
            localStorage.clear();
            navigate('/RawAuth');
          }}
          onChangePassword={() => {}}
          onSettings={() => navigate('/rmrdc/settings')}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header Section */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ReceiptIcon sx={{ color: COLORS.accent, fontSize: 32 }} />
                  Requests Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {totalCount} Requests found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {selectedOrders.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setBulkActionDialog(true)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Bulk Action ({selectedOrders.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Refresh
                </Button>
             
              </Box>
            </Box>
          </Box>

          {/* Filters */}
          {renderFilters()}

          {/* Bulk Actions */}
          {renderBulkActions()}

          {/* Orders Table */}
          {renderOrdersTable()}
        </Box>

        <Footer />
      </Box>

      {/* ============================================
          DIALOGS
          ============================================ */}

      {/* View Order Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        {selectedOrder && (
          <>
            <DialogTitle sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Requests #{selectedOrder.id}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedOrder.order_reference || `ORD-${selectedOrder.id}`}
                  </Typography>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Order Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Requests Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Status</Typography>
                        <Chip
                          size="small"
                          label={getStatusChip(selectedOrder.status).label}
                          sx={{
                            bgcolor: getStatusChip(selectedOrder.status).bg,
                            color: getStatusChip(selectedOrder.status).text,
                            fontWeight: 600,
                          }}
                          icon={getStatusChip(selectedOrder.status).icon}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Payment</Typography>
                        <Chip
                          size="small"
                          label={getPaymentStatusChip(selectedOrder.payment_status).label}
                          sx={{
                            bgcolor: getPaymentStatusChip(selectedOrder.payment_status).bg,
                            color: getPaymentStatusChip(selectedOrder.payment_status).text,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Date</Typography>
                        <Typography variant="body2">{formatDate(selectedOrder.order_date)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Total</Typography>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {formatCurrency(selectedOrder.total_amount, selectedOrder.currency)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Buyer Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Buyer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                        <Typography variant="body2">{selectedOrder.buyer_name || 'Guest'}</Typography>
                      </Box>
                      {selectedOrder.buyer_organisation && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                          <Typography variant="body2">{selectedOrder.buyer_organisation}</Typography>
                        </Box>
                      )}
                      {selectedOrder.buyer_email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                          <Typography variant="body2">{selectedOrder.buyer_email}</Typography>
                        </Box>
                      )}
                      {selectedOrder.shipping_address && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                          <Typography variant="body2">{selectedOrder.shipping_address}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Order Items */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Requests Items ({selectedOrder.total_items || 0})
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="center">Quantity</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {item.product_image && (
                                    <Box
                                      component="img"
                                      src={item.product_image}
                                      alt={item.product_name}
                                      sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover' }}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2">{item.product_name || `Product #${item.product_id}`}</Typography>
                                    {item.supplier_name && (
                                      <Typography variant="caption" color="textSecondary">
                                        Supplier: {item.supplier_name}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">{item.quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.price, selectedOrder.currency)}</TableCell>
                              <TableCell align="right" fontWeight={700}>
                                {formatCurrency(item.total, selectedOrder.currency)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No items found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Order History */}
                {selectedOrder.history && selectedOrder.history.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Requests History
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2, maxHeight: 200, overflow: 'auto' }}>
                      {selectedOrder.history.map((history, index) => (
                        <Box
                          key={history.id || index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: index < selectedOrder.history.length - 1 ? `1px solid ${COLORS.grey[200]}` : 'none',
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {history.status || history.action || 'Updated'}
                            </Typography>
                            {history.notes && (
                              <Typography variant="caption" color="textSecondary">
                                {history.notes}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(history.created_at)}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleEditOrder(selectedOrder);
                }}
                sx={{ bgcolor: COLORS.primary }}
              >
                Edit Status
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Update Requests Status
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Requests #{editOrderData?.id}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Status
              </Typography>
              <Select
                value={editOrderData?.status || 'pending'}
                onChange={(e) => setEditOrderData({ ...editOrderData, status: e.target.value })}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Payment Status
              </Typography>
              <Select
                value={editOrderData?.payment_status || 'pending'}
                onChange={(e) => setEditOrderData({ ...editOrderData, payment_status: e.target.value })}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={editOrderData?.notes || ''}
              onChange={(e) => setEditOrderData({ ...editOrderData, notes: e.target.value })}
              placeholder="Add notes about this status change..."
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Update Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete/Cancel Order Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: COLORS.error }} />
            <Typography variant="h6" fontWeight={700}>Cancel Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600] }}>
            Are you sure you want to cancel Request <strong>#{selectedOrder?.id}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            This action will mark the Request as cancelled and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteOrder(selectedOrder?.id)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialog}
        onClose={() => setBulkActionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Bulk Action</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600], mb: 2 }}>
            Select action for {selectedOrders.length} orders:
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>Select action</MenuItem>
              <MenuItem value="process">Process All</MenuItem>
              <MenuItem value="ship">Ship All</MenuItem>
              <MenuItem value="deliver">Deliver All</MenuItem>
              <MenuItem value="cancel">Cancel All</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={bulkUpdateOrders}
            disabled={!bulkAction}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Apply to {selectedOrders.length} Orders
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 3, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RmrdcOrders;